import React, { useState, useEffect, createContext, useContext } from 'react';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KingdomTitlesContext = createContext();

export const useKingdomTitles = () => useContext(KingdomTitlesContext);

export const KingdomTitlesProvider = ({ children }) => {
  const [titles, setTitles] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTitles();
    const interval = setInterval(fetchTitles, 120000);
    return () => clearInterval(interval);
  }, []);

  const fetchTitles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/kingdom/titles`);
      if (res.ok) {
        const data = await res.json();
        setTitles(data.titles || {});
      }
    } catch (err) {
      logger.error('Failed to fetch kingdom titles:', err);
    } finally {
      setLoading(false);
    }
  };

  const getUserTitle = (userId) => {
    if (!userId || !titles[userId]) return null;
    return titles[userId][0] || null;
  };

  const getUserTitles = (userId) => {
    if (!userId || !titles[userId]) return [];
    return titles[userId];
  };

  return (
    <KingdomTitlesContext.Provider value={{ titles, loading, getUserTitle, getUserTitles, refreshTitles: fetchTitles }}>
      {children}
    </KingdomTitlesContext.Provider>
  );
};

const TitleBadge = ({ title, size = 'sm' }) => {
  if (!title) return null;

  const isKing = title.type === 'king';
  const sizeClass = size === 'lg' ? 'title-badge--lg' : '';

  return (
    <span
      className={`title-badge ${isKing ? 'title-badge--king' : 'title-badge--duke'} ${sizeClass}`}
      data-testid={`title-badge-${title.type}`}
      title={title.label}
    >
      {isKing ? '👑' : '🛡️'} {title.label}
    </span>
  );
};

export const TitleBadgeInline = ({ userId, size = 'sm' }) => {
  const ctx = useContext(KingdomTitlesContext);
  if (!ctx) return null;
  const title = ctx.getUserTitle(userId);
  if (!title) return null;
  return <TitleBadge title={title} size={size} />;
};

export default TitleBadge;
